import { ADMIN_NAV, NAV, type NavChild } from './nav.js';

export interface Breadcrumb {
  root: 'Portal';
  section?: string;
  page?: string;
  /** Resolved from a matched `NavChild`'s `tabs` array (Asset > Administration's pill tabs) via the
   * same longestPrefixMatch helper below. */
  subpage?: string;
}

interface Crumbable {
  to: string;
  label: string;
}

/**
 * Longest-prefix match of `pathname` against `candidates` by `to` — an exact match wins over a
 * prefix match by virtue of being longer (e.g. `/performance/goals` beats `/performance` for
 * `/performance/goals/g1`), and a prefix only counts at a path-segment boundary (`/performance`
 * matches `/performance/goals` but not `/performance-review`).
 */
function longestPrefixMatch<T extends Crumbable>(
  pathname: string,
  candidates: readonly T[],
): T | undefined {
  let best: T | undefined;
  for (const candidate of candidates) {
    const isMatch = pathname === candidate.to || pathname.startsWith(`${candidate.to}/`);
    if (isMatch && (!best || candidate.to.length > best.to.length)) {
      best = candidate;
    }
  }
  return best;
}

/**
 * Derives the topbar breadcrumb trail purely from the current pathname — there is no React
 * Router `handle` metadata to hook into here (`App.tsx` uses plain `<Routes>`/`<Route>` JSX, not
 * `createBrowserRouter`), so this matches against the same `NAV`/`ADMIN_NAV` data the sidebar
 * already renders from, rather than duplicating route metadata anywhere.
 *
 * The menu is organised by module (D3), so the trail is "module > page": every module's pages
 * (`children`, or `subItems` for the one-page-many-tabs modules like Attendance) are searched,
 * the employee menu before the admin one — a self-service page routed under /admin (Tax
 * Declarations) reads as Payroll > Tax Declarations. Every module is checked, not just the first:
 * a previous version stopped at the first item with children and sent every /performance/* and
 * /asset-management/* page to just "Portal". A page with no match at all (e.g. Home) resolves to
 * just `{root}`.
 */
export function resolveBreadcrumb(pathname: string): Breadcrumb {
  const root: Breadcrumb['root'] = 'Portal';

  // The LONGEST match across every module wins, the employee menu first on a tie: a page can sit
  // under a shorter employee prefix (the desk's /travel/desk/teams under My trips' /travel) and
  // belongs to the module whose entry names it most exactly.
  let best:
    | { item: (typeof NAV)[number]; match: Crumbable & { tabs?: NavChild['tabs'] } }
    | undefined;
  for (const menu of [NAV, ADMIN_NAV]) {
    for (const item of menu) {
      const pages: readonly (Crumbable & { tabs?: NavChild['tabs'] })[] =
        item.children ??
        (item.subItems ?? []).map((sub) => ({ to: `${item.to}/${sub.path}`, label: sub.label }));
      const match = longestPrefixMatch(pathname, pages);
      if (match && (!best || match.to.length > best.match.to.length)) best = { item, match };
    }
  }
  if (best) {
    const { item, match } = best;
    if (match.tabs) {
      const tabMatch = longestPrefixMatch(pathname, match.tabs);
      if (tabMatch) {
        return { root, section: item.label, page: match.label, subpage: tabMatch.label };
      }
    }
    return { root, section: item.label, page: match.label };
  }

  const adminMatch = longestPrefixMatch(pathname, ADMIN_NAV);
  if (adminMatch) {
    return { root, section: 'Admin', page: adminMatch.label };
  }

  return { root };
}
